// #MODULE - PostFor
//
// > Author: Cristopher Moreira da Silva
// >
// > Create Date: March - 2014
// >
// #DESCRIPTION: A component that receives a couple of options
// from SpeedBumpOverlay and then posts to specific citi urls
// using parameters from these options and from an ajax call
// that brings further data to send.
//
import Cookie from '@component/common/src/util/Cookie';
import Logger from '@component/common/src/util/Logger';
import { element } from '../util/element';
import { clearApiCache } from './clearCache';
import { xAPISetUpRequest } from './setupRequests';
import { getDksKillswitches, dksCheck } from './dksCheck';

const guid = Cookie.get('macys_online_guid') ? Cookie.get('macys_online_guid') : Cookie.get('bloomingdales_online_guid');

// ###Private Method redirectUser( href )
//
// > parameters
// >
// + *href* - URL user should be redirected to
//
// > returns
// >
// + Nothing. User will be redirected.
function redirectUser(href) {
  window.location.href = href;
}

// ###Private Method submitWith( module, options ) - constructor
//
// > parameters
// >
// + *module* - All the data that comes from backend via ajax call,
// + enriched with some data from front end, at postFor. Data used
// + to build the form (POST CALL).
//
// + *callbacks* - A list of callbacks to be executed on form submit (pre POST call)
// + *callback* - Alternate key to set a callback, if you have just one.
// + If you pass both callbacks and callback, callback is appended to
// + callbacks and all callbacks are executed.
// + *container* - A container to append the created form. Defaults to body.
//
// > returns
// >
// + Nothing. When the form submits the page is redirected.
function submitWith(module, options = {}) {
  // clear api cache once we fire deeplink
  clearApiCache(options.action);

  // Creates a detached form object
  const formElement = element('form', {
    method: 'post',
    action: module.postURL,
  });
  const documentFragment = document.createDocumentFragment();
  delete module.postURL; // eslint-disable-line no-param-reassign
  Object.entries(module).forEach(([name, value]) => {
    if (typeof value === 'object') {
      value = JSON.stringify(value); // eslint-disable-line no-param-reassign
    }
    // Skips empty values
    // (We can't use if(!value) because it would skip 0)
    if (typeof value === 'undefined' || value === null || value === '' || value === 'null') {
      return;
    }
    const inputElement = document.createElement('input');
    inputElement.setAttribute('type', 'hidden');
    inputElement.setAttribute('name', name);
    inputElement.setAttribute('value', value);
    documentFragment.appendChild(inputElement);
  });
  formElement.append(documentFragment);
  // Note that this line doesn't init the options.callbacks
  // if it's undefined.
  const callbacks = options.callbacks || [];
  if (options.callback) {
    callbacks.push(options.callback);
  }
  callbacks.forEach((callback) => {
    formElement.addEventListener('submit', callback);
  });
  // Hides it
  formElement.style.display = 'none';
  // Appends it to a container (hidden)
  // (Detached forms can't be submitted)
  let container = options;
  if (!(container instanceof HTMLElement)) {
    // container might be a jQuery object
    container = (container && container[0]) || document.body;
  }
  container.append(formElement);
  // Add a delay so that tag.js call and profiling is complete before redirecting.
  setTimeout(() => {
    formElement.submit();
  }, 2000);
  // There's no need to dispose of the form because
  // we are being redirected.
}

// ###Method ajaxError( remoteURL, textStatus, error) - Notifies that an error happened on ajaxCall. Yet to be implemented.
//
// > parameters
// >
// + *remoteURL* - The ajax url that has failed. If blank, the ajax call didn't even happen. Just prints textStatus, that must be a full message.
// + *textStatus* - Jquery textStatus.
// + *error* - Jquery error.
//
// > returns
// >
// + Nothing.
//
function ajaxError(remoteURL, textStatus, error) {
  let errorToPrint;
  if (!remoteURL) {
    console.error(textStatus);
    return;
  }
  errorToPrint = `Failed to get properties from ${remoteURL}`;
  if (textStatus) {
    errorToPrint += ` with text status '${textStatus}'`;
  }
  if (error) {
    errorToPrint += `, with error '${error}'`;
  }
  errorToPrint += '.';
  console.error(errorToPrint);

  // This shows a generic "Oops, we are having technical difficulties..."
  // message in SpeedBumpOverlay.
  // The done callback runs when/if call is successful
  // This renders checks the response to see if there are any errors,
  // if errors, they are displayed in OopsOverlay

  const genericErrorMsg = document.querySelector('#genericErrorMsg');
  if (genericErrorMsg) {
    genericErrorMsg.classList.remove('hide');
  }
  const overlaySpinner = document.querySelector('#overlay-spinner');
  if (overlaySpinner) {
    overlaySpinner.remove();
  }
  const headerWrapper = document.querySelector('.header-wrapper');
  if (headerWrapper) {
    headerWrapper.style.display = 'block';
  }
  const overlayCloseButton = document.querySelector('.overlay-close-btn');
  if (overlayCloseButton) {
    overlayCloseButton.focus();
  }
}

// Returns true if the action is a non-SSO action
function isNonSso(action) {
  return ['activateCard', 'activation', 'acquisition', 'applynow', 'applyNow', 'verify'].indexOf(action) > -1;
}

function userIsSignedOut(action, response) {
  let isSignedOut = false;
  const signedOutResponseHeader = response.headers ? response.headers.get('SESSION_TIMED_OUT') : false;

  // For non-SSO action, user can be a guest
  if (isNonSso(action)) {
    isSignedOut = false;
  } else if (signedOutResponseHeader) {
    isSignedOut = true;
  } else if (response.creditDeeplinkData && (response.creditDeeplinkData.signedIn === false)) {
    isSignedOut = true;
  } else if (response.creditEmailDeeplinkData && (response.creditEmailDeeplinkData.signedIn === false)) {
    isSignedOut = true;
  }

  return isSignedOut;
}

async function setupRequest(options) {
  let configs;
  // If we already know whether or not to use xAPI, dont make DKS call to get KS value
  if (options.useXApi === true) {
    configs = xAPISetUpRequest(options);
    Logger.info('Dont make DKS call to get KS value');
    Logger.log('Dont make DKS call to get KS value');
  } else {
    try {
      const killswitches = await getDksKillswitches();
      options.creditTMXEnabled = await dksCheck('creditTMXEnabled', killswitches);
      Logger.info('Killswitches for creditTMXEnabled:', killswitches);
      Logger.log('Killswitches for creditTMXEnabled:', killswitches);
      configs = xAPISetUpRequest(options);
    } catch (e) {
      configs = xAPISetUpRequest(options);
      Logger.info('DKS call failed for getting KS creditTMXEnabled');
      Logger.log('DKS call failed for getting KS creditTMXEnabled');
    }
  }

  return configs;
}

function makeRequest(options, ajaxRequestSettings, OopsOverlayView) {
  const {
    action, useXApi, isEmailDeepLink, creditTMXEnabled,
  } = options;
  const requestOptions = {};
  const {
    headers, method, url, data,
  } = ajaxRequestSettings;
  let ajaxURL = url || '';
  requestOptions.headers = headers;
  requestOptions.method = method;
  if (method.toLowerCase() === 'post') {
    requestOptions.body = JSON.stringify(data);
  } else {
    const params = `${new URLSearchParams(data)}`;
    ajaxURL += `${ajaxURL.includes('?') ? '&' : '?'}${params}`;
  }

  // undefined is the success callback, that is going on done() now.
  return fetch(ajaxURL, requestOptions).then((response) => {
    let location;
    let statusCode;
    let statusText;
    if (!response.ok) {
      statusCode = response.status ? response.status.toString() : '';
      statusText = response.statusText;
    }
    // Check if user is signed out
    // NOTE for non-SSO links, user can be a guest and proceed to Citi
    if (userIsSignedOut(action, response)) {
      location = options.location || window.location;
      location.href = `${location.origin}/account/signin`;
      // do not set cookie if this is an email deeplink using xAPI
      // xAPI will set the cookie as part of emaildeeplink xAPI
      // if user is signed out
      if (!useXApi && !isEmailDeepLink) {
        document.cookie = `FORWARDPAGE_KEY=${encodeURIComponent(location.href)}; path=/; secure; domain=${document.location.host.replace('www', '')}`;
      }
      return false;
    }
    if (statusCode) {
      ajaxError(`Error: ${statusText}`);
      return false;
    }
    return response.json();
  }).then((result) => {
    if (!result) return false;
    // Getting objKeys in result object
    const resultObjkey = Object.keys(result);
    result = result[resultObjkey[0]]; // eslint-disable-line no-param-reassign
    if (result.error) {
      new OopsOverlayView({
        error: result.error,
      }).render();
      return false;
    }
    if (!result || !Object.keys(result).length) {
      ajaxError(ajaxURL);
      return false;
    }
    if (result && options.fromOTR && !result.signedIn) {
      redirectUser(`${window.location.origin}/account/signin`);
      return false;
    }
    if (result && result.oneTimeRegStatus === 'C') {
      redirectUser(`${window.location.origin}/my-credit/gateway`);
      return false;
    }
    result.postURL = result.partnerDeepLink || null; // eslint-disable-line no-param-reassign
    if (!result.postURL) {
      ajaxError(ajaxURL, `There is no postURL for the action ${action} registered.`);
      return false;
    }

    // Boolean to binary
    result.signedIn = result.signedIn ? 1 : 0; // eslint-disable-line no-param-reassign

    // get blackbox and userCookieValue and tmx session id
    if (action !== 'activateCard' && action !== 'applyNow' && action !== 'verify') {
      if (typeof window.ioGetBlackbox === 'function') {
        result.blackbox = window.ioGetBlackbox().blackbox; // eslint-disable-line no-param-reassign
      } else {
        result.blackbox = ''; // eslint-disable-line no-param-reassign
      }

      // TMX
      if (creditTMXEnabled) {
        if (typeof window.getNewTmxSessionId === 'function') {
          result.tmx_sessionId = window.getNewTmxSessionId();
          Logger.info('TMX Session ID is generated', result.tmx_sessionId);
          Logger.log('TMX Session ID is generated', result.tmx_sessionId);
        } else {
          result.tmx_sessionId = '';
          Logger.info(`TMX Session ID is null on makeRequest() for user guid: ${guid} with sessionId:`, result.tmx_sessionId);
          Logger.log(`TMX Session ID is null on makeRequest() for user guid: ${guid} with sessionId:`, result.tmx_sessionId);
        }
      }

      //  Updated the devicePrint from the vendor script.
      // TODO -- need to find another devicePrint module
      // result.devicePrint = DevicePrint.getDevicePrint() || "";
      result.devicePrint = ''; // eslint-disable-line no-param-reassign
    }
    // deleting unnecessary fields
    delete result.partnerDeepLink; // eslint-disable-line no-param-reassign
    delete result.userCookieValue; // eslint-disable-line no-param-reassign
    delete result.pageIndex; // eslint-disable-line no-param-reassign
    delete result.messages; // eslint-disable-line no-param-reassign
    delete result.oneTimeRegStatus; // eslint-disable-line no-param-reassign
    delete result.requestFromEmail; // eslint-disable-line no-param-reassign
    // Defer to submitWith
    // Only submit the form if there are no errors,
    // If there are errors, then display them in OopsOverlayView
    return submitWith(result, options || {});
  }).catch((error) => {
    const textStatus = 'error';
    ajaxError(ajaxURL, textStatus, error);
  });
}

async function postFor(options, OopsOverlayView) {
  // Campaign Code Project depends on a 'acquisitionOrigin' Key in sessionStorage.
  // After user successfully navigates to Citi, we need to clear this value
  if (options.action === 'applyNow' && sessionStorage) {
    sessionStorage.removeItem('acquisitionOrigin');
  }

  const ajaxRequestSettings = await setupRequest(options);
  await makeRequest(options, ajaxRequestSettings, OopsOverlayView);
}

// TODO: figure out why this is throwing error TypeError: Cannot assign to read only property 'exports' of object
// module.exports = postFor;

/* eslint-disable */
export {
  setupRequest,
  postFor,
  submitWith,
  redirectUser,
};
/* eslint-enable */
